import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  ShieldCheck,
  Sparkles,
  MapPin,
  CalendarCheck2,
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  Briefcase,
  HardHat,
  Smartphone,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import QRCode from "qrcode";
import { useI18n } from "../i18n/I18nContext";
import { LanguageToggle } from "../components/LanguageToggle";
import { toast } from "sonner";
import { PWAInstallBanner } from "../components/PWAInstallBanner";
import { usePlatformPortalTrigger } from "../hooks/usePlatformPortalTrigger";

// Curated Unsplash photos — clean homes / cleaning service feel.
// Stable CDN URLs (status 200 verified).
const SLIDES = [
  {
    src: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=1800&q=80&auto=format&fit=crop",
    label: "Limpeza profissional",
  },
  {
    src: "https://images.unsplash.com/photo-1600210492493-0946911123ea?w=1800&q=80&auto=format&fit=crop",
    label: "Banheiro brilhando",
  },
  {
    src: "https://images.unsplash.com/photo-1554995207-c18c203602cb?w=1800&q=80&auto=format&fit=crop",
    label: "Ambiente arejado",
  },
  {
    src: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1800&q=80&auto=format&fit=crop",
    label: "Pronto para o cliente",
  },
];
const SLIDE_DURATION = 6000;

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export default function Login() {
  const { login, logout } = useAuth();
  const { t } = useI18n();
  const portalTrigger = usePlatformPortalTrigger();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const queryAs = searchParams.get("as");
  const queryOverride =
    queryAs === "collaborator" || queryAs === "admin" ? queryAs : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [accessType, setAccessType] = useState(() => {
    try {
      // Query string tem prioridade máxima (deep link).
      if (
        typeof window !== "undefined" &&
        window.location?.search
      ) {
        const q = new URLSearchParams(window.location.search).get("as");
        if (q === "collaborator" || q === "admin") return q;
      }
      const saved = localStorage.getItem("cm:lastAccessType");
      if (saved === "collaborator" || saved === "admin") return saved;
      // Sem preferência: se PWA instalado, presume colaborador (uso em campo).
      if (typeof window !== "undefined" && window.matchMedia) {
        const isStandalone =
          window.matchMedia("(display-mode: standalone)").matches ||
          window.navigator.standalone === true;
        if (isStandalone) return "collaborator";
      }
      return "admin";
    } catch {
      return "admin";
    }
  });

  // Quando o query string muda (ex: usuário troca o link), sincroniza a aba.
  useEffect(() => {
    if (queryOverride && queryOverride !== accessType) {
      setAccessType(queryOverride);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryOverride]);
  const [slide, setSlide] = useState(0);
  const [installQr, setInstallQr] = useState("");

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(window.location.origin + "/instalar-celular", {
      width: 200,
      margin: 1,
      color: { dark: "#0F172A", light: "#FFFFFF" },
    })
      .then((url) => {
        if (alive) setInstallQr(url);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Persiste a última aba escolhida.
  function changeAccessType(next) {
    setAccessType(next);
    setErr("");
    try {
      localStorage.setItem("cm:lastAccessType", next);
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    // Pre-cache the next images so the cross-fade is smooth.
    SLIDES.forEach((s) => {
      const img = new Image();
      img.src = s.src;
    });
    const id = setInterval(() => {
      setSlide((s) => (s + 1) % SLIDES.length);
    }, SLIDE_DURATION);
    return () => clearInterval(id);
  }, []);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const u = await login(email, password);
      // Super-admin NÃO entra pelo login regular. Mantém a existência do
      // portal Platform Admin oculta dos usuários comuns: trata como
      // credencial inválida e derruba a sessão imediatamente.
      if (u.is_super_admin) {
        await logout();
        setErr(t("E-mail ou senha inválidos"));
        return;
      }
      // Verifica se o tipo de acesso bate com o role
      const isCollaborator = u.role === "collaborator";
      if (accessType === "admin" && isCollaborator) {
        setErr(
          t('Esta conta é de colaborador. Use a aba "Colaborador" para acessar o app de campo.'),
        );
        return;
      }
      if (accessType === "collaborator" && !isCollaborator) {
        setErr(
          t('Esta conta é administrativa. Use a aba "Admin / Supervisor" para acessar o painel.'),
        );
        return;
      }
      toast.success(t("Bem-vindo(a), {name}", { name: u.name }));
      // Reseta contador de falhas após sucesso
      setFailedAttempts(0);
      // Persiste o tipo de acesso correto baseado no role real (caso conta seja híbrida)
      try {
        localStorage.setItem(
          "cm:lastAccessType",
          isCollaborator ? "collaborator" : "admin",
        );
      } catch {
        // ignore
      }
      nav(isCollaborator ? "/mobile" : "/dashboard");
    } catch (e) {
      setErr(e.message);
      setFailedAttempts((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  function fillDemo() {
    setEmail("admin@cleanmanager.com");
    setPassword("admin123");
    setErr("");
  }

  return (
    <div
      className="min-h-screen w-full bg-[#F8FAFC] grid grid-cols-1 lg:grid-cols-12 font-[Manrope]"
      data-testid="login-page"
    >
      {/* ── FORM SIDE ─────────────────────────────────────────────── */}
      <div className="lg:col-span-5 flex flex-col justify-center px-5 sm:px-10 lg:px-12 xl:px-20 py-10 lg:py-12 bg-white relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="w-full max-w-md mx-auto"
        >
          {/* Logo + brand (triple-click discreto → portal Platform Admin) */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col gap-4 mb-10 sm:flex-row sm:items-center sm:justify-between"
          >
            <div
              className="flex items-center gap-3 cursor-default select-none"
              onClick={portalTrigger}
              data-testid="login-logo-trigger"
            >
              <img
                src="/assets/cleanmanager-emblem.png"
                alt="Clean Manager USA"
                className="h-20 w-20 sm:h-16 sm:w-16 object-contain shrink-0"
                draggable={false}
              />
              <div className="leading-tight min-w-0">
                <div className="font-[Outfit] font-bold text-slate-900 text-xl sm:text-lg whitespace-nowrap">
                  Clean Manager <span className="text-blue-600">USA</span>
                </div>
                <div className="text-[11px] tracking-[0.16em] uppercase text-slate-400 font-medium">
                  {t("Operações de limpeza")}
                </div>
              </div>
            </div>
            <div className="shrink-0 self-start sm:self-auto">
              <LanguageToggle compact testId="login-language-toggle" />
            </div>
          </motion.div>

          {/* Voltar ao site público */}
          <motion.div variants={itemVariants} className="-mt-6 mb-6">
            <Link
              to="/"
              data-testid="login-home-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600 transition"
            >
              <ArrowLeft size={15} /> {t("Voltar ao site")}
            </Link>
          </motion.div>

          {/* Headline */}
          <motion.div variants={itemVariants} className="mb-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.16em] uppercase text-blue-700 font-semibold bg-blue-50 border border-blue-100 px-2.5 py-1 rounded-full">
              <Sparkles size={12} /> {t("Bem-vindo de volta")}
            </span>
          </motion.div>
          <motion.h1
            variants={itemVariants}
            className="font-[Outfit] text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 mt-3"
          >
            {t("Entre no seu painel.")}
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="text-sm md:text-base text-slate-500 mt-2 mb-6"
          >
            {t("Acesse sua conta para gerenciar equipes, agenda e financeiro — ou iniciar sua próxima limpeza com geofence em tempo real.")}
          </motion.p>

          {/* Access type tabs */}
          <motion.div
            variants={itemVariants}
            data-testid="access-type-tabs"
            className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-slate-100 mb-6"
          >
            <button
              type="button"
              data-testid="access-tab-admin"
              onClick={() => changeAccessType("admin")}
              className={`group flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
                accessType === "admin"
                  ? "bg-white text-blue-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              <Briefcase
                size={16}
                className={accessType === "admin" ? "text-blue-600" : ""}
              />
              Admin / Supervisor
            </button>
            <button
              type="button"
              data-testid="access-tab-collaborator"
              onClick={() => changeAccessType("collaborator")}
              className={`group flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
                accessType === "collaborator"
                  ? "bg-white text-emerald-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              <HardHat
                size={16}
                className={accessType === "collaborator" ? "text-emerald-600" : ""}
              />
              {t("Colaborador")}
            </button>
          </motion.div>

          {/* Access type explainer */}
          <motion.div variants={itemVariants} className="mb-4">
            {accessType === "admin" ? (
              <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-blue-50/60 border border-blue-100">
                <Briefcase size={15} className="text-blue-600 shrink-0 mt-0.5" />
                <div className="text-xs text-slate-700 leading-snug">
                  <strong className="text-blue-700">{t("Painel administrativo:")}</strong>{" "}
                  {t("agenda, clientes, equipes, financeiro e relatórios.")}
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-emerald-50/60 border border-emerald-100">
                <HardHat size={15} className="text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-xs text-slate-700 leading-snug">
                  <strong className="text-emerald-700">{t("App de campo:")}</strong>{" "}
                  {t("suas limpezas do dia, check-in com GPS, fotos antes/depois.")}
                </div>
              </div>
            )}
          </motion.div>

          {/* PWA install banner (apenas quando aplicável) */}
          <motion.div variants={itemVariants}>
            <PWAInstallBanner />
            <div
              className="mt-2 flex items-center gap-3 p-3 rounded-2xl border border-slate-200 bg-slate-50/60"
              data-testid="login-install-card"
            >
              {installQr ? (
                <img
                  src={installQr}
                  alt="QR Code para instalar no celular"
                  data-testid="login-install-qr"
                  className="w-16 h-16 rounded-lg bg-white p-1 border border-slate-200 shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-slate-200 animate-pulse shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                  <Smartphone size={13} /> {t("Instalar no celular")}
                </div>
                <div className="text-[11px] text-slate-500 leading-snug mt-0.5">
                  {t("Aponte a câmera do celular para o QR Code")}
                </div>
                <Link
                  to="/instalar-celular"
                  data-testid="login-install-mobile-link"
                  className="text-[11px] font-semibold text-blue-600 hover:underline"
                >
                  {t("ou toque para ver o passo a passo →")}
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            variants={itemVariants}
            onSubmit={submit}
            className="space-y-5"
            data-testid="login-form"
          >
            <div>
              <label
                htmlFor="login-email"
                className="block text-sm font-medium text-slate-900 mb-1.5"
              >
                {t("E-mail")}
              </label>
              <input
                id="login-email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="voce@empresa.com"
                className="h-12 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-200 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/15"
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <label
                  htmlFor="login-password"
                  className="block text-sm font-medium text-slate-900"
                >
                  {t("Senha")}
                </label>
                <button
                  type="button"
                  data-testid="login-forgot"
                  onClick={() => nav("/forgot-password")}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                >
                  {t("Esqueci minha senha")}
                </button>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="h-12 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 pr-12 text-slate-900 placeholder:text-slate-400 outline-none transition-all duration-200 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/15"
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  aria-label={showPassword ? t("Ocultar senha") : t("Mostrar senha")}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {err && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="login-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3.5 py-2.5 space-y-2"
              >
                <div>{err}</div>
                {failedAttempts >= 1 && accessType === "admin" && !err.toLowerCase().includes("google") && (
                  <div
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t border-red-200"
                    data-testid="login-recovery-hint"
                  >
                    <span className="text-xs text-red-700/80">
                      {t("Esqueceu a senha? Recupere por e-mail em 1 minuto.")}
                    </span>
                    <button
                      type="button"
                      data-testid="login-recovery-cta"
                      onClick={() => nav("/forgot-password", { state: { email } })}
                      className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-white border border-red-300 text-red-700 hover:bg-red-100 px-3 py-1.5 rounded-lg transition shrink-0"
                    >
                      {t("Recuperar agora →")}
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={busy}
              className={`group h-12 w-full disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] ${
                accessType === "collaborator"
                  ? "bg-emerald-600 hover:bg-emerald-700 shadow-[0_8px_24px_-12px_rgba(5,150,105,0.6)]"
                  : "bg-slate-900 hover:bg-slate-800 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.5)]"
              }`}
            >
              {busy ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {t("Entrando…")}
                </>
              ) : (
                <>
                  {t("Entrar")}
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />
                </>
              )}
            </button>

            {accessType === "admin" && (
              <div className="text-center pt-2">
                <span className="text-xs text-slate-500">
                  {t("Ainda não tem conta?")}{" "}
                </span>
                <Link
                  to="/signup"
                  data-testid="login-to-signup"
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {t("Criar empresa grátis →")}
                </Link>
              </div>
            )}
          </motion.form>

          <motion.div
            variants={itemVariants}
            className="mt-10 flex items-center gap-5 text-xs text-slate-400"
          >
            <div className="flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-emerald-500" /> {t("Login seguro")}
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin size={14} className="text-blue-500" /> Geofence GPS
            </div>
            <div className="hidden sm:flex items-center gap-1.5">
              <CalendarCheck2 size={14} className="text-violet-500" /> {t("Agenda ao vivo")}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ── VISUAL SIDE (desktop only) ────────────────────────────── */}
      <div className="hidden lg:block lg:col-span-7 relative overflow-hidden bg-[#001340]">
        {/* Ken Burns rotating photo carousel */}
        <AnimatePresence mode="sync">
          <motion.div
            key={slide}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
          >
            <motion.img
              src={SLIDES[slide].src}
              alt=""
              aria-hidden="true"
              initial={{ scale: 1.08, x: 0, y: 0 }}
              animate={{ scale: 1.22, x: -20, y: -10 }}
              transition={{
                duration: SLIDE_DURATION / 1000 + 1.5,
                ease: "linear",
              }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        </AnimatePresence>

        {/* Dark overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#001340]/85 via-[#001340]/55 to-[#001340]/30" />

        {/* Floating glass cards */}
        <div className="relative h-full w-full p-12 xl:p-16 flex flex-col justify-between text-white">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="flex items-center justify-between gap-4"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={`label-${slide}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-medium"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                {t(SLIDES[slide].label)}
              </motion.div>
            </AnimatePresence>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {t("Sistema operacional · v2026")}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="max-w-xl"
          >
            <h2 className="font-[Outfit] text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight">
              {t("Sua operação de limpeza,")}
              <br />
              <span className="text-blue-300">{t("organizada e no controle.")}</span>
            </h2>
            <p className="mt-5 text-slate-200/90 text-base xl:text-lg max-w-lg leading-relaxed">
              {t("Agenda, equipes, GPS, fotos antes/depois e relatórios — em um único painel pensado para celular e desktop.")}
            </p>

            {/* Stat chips */}
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {[
                { k: "200m", v: t("Raio de geofence") },
                { k: "PWA", v: t("Funciona offline") },
                { k: "24/7", v: t("Acesso em campo") },
              ].map((s, i) => (
                <motion.div
                  key={s.k}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 + i * 0.08, duration: 0.45 }}
                  className="px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 hover:bg-white/15 transition-colors"
                >
                  <div className="font-[Outfit] text-xl font-semibold">{s.k}</div>
                  <div className="text-[11px] text-slate-200 mt-0.5 leading-tight">
                    {s.v}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Carousel indicators + footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="flex items-center justify-between text-xs text-slate-300/80"
          >
            <div className="flex items-center gap-2">
              {SLIDES.map((s, i) => (
                <button
                  key={`slide-${s.id || i}`}
                  type="button"
                  aria-label={`Ir para slide ${i + 1}`}
                  onClick={() => setSlide(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === slide ? "w-8 bg-white" : "w-4 bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
            <div>
              © {new Date().getFullYear()} Clean Manager USA
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
